import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Me, type WxcStatus, type WxccStatus, type WxcLocalGateway, type WxccEntryPointNumbers, type SpokeRow, type CucmPhone, type LgwGateway, type LgwCert, type LgwSyncRun, type CubeSipTrunk, type WxccInteractionLookupResponse, type WxccInteractionsByNumberResponse, type WxcCallsResponse } from '../lib/api';

/**
 * Cross-system operations tool — runs a LIVE health scan against the existing
 * module-service APIs (wxc-svc, wxcc-svc, lgw-svc, spoke-svc) per sub-tab,
 * surfaces detected issues, and assembles a Cowork-ready hand-off package
 * (Copy to clipboard / Download .md) for any finding or free-text symptom.
 *
 * No mock/seed/demo data. No new external integrations. If a backing API is
 * unreachable, the unreachability is itself surfaced as a finding so the
 * admin still has something to hand off.
 */

type AreaKey = 'wxc' | 'wxcc' | 'cucm' | 'uccx' | 'lgw';
type Severity = 'ok' | 'warn' | 'error';
// `default` → the persona/diagnostic-steps prompt.
// `action-items` → WxCC Flow Troubleshooting handoffs that must return root
// cause + concrete numbered action items (per BUILD-TASK §2b).
type AnalysisMode = 'default' | 'action-items';

interface Finding {
  id: string;
  severity: Severity;
  title: string;
  evidence: string;
  action: string;
  evidenceData?: unknown;
  // Where this finding came from — used by buildPackage to choose prompt + headers.
  kind?: 'health-scan' | 'symptom' | 'wxcc-interaction' | 'wxcc-tracking' | 'wxcc-number' | 'wxc-call' | 'wxc-call-detail';
  analysisMode?: AnalysisMode;
}

// ---------------------------------------------------------------------------
// Attachments (HAR + log). Parsed CLIENT-SIDE in the browser. Capped at
// MAX_ATTACHMENT_BYTES so a runaway HAR doesn't lock the tab; parsers handle
// malformed files gracefully and surface a parse_error string in that case.
// ---------------------------------------------------------------------------
const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;     // 15 MB cap (per BUILD-TASK §2c)
const HAR_RAW_MD_LIMIT     = 64 * 1024;            // inline HAR raw bytes in .md only when ≤64 KB; bigger HARs are emitted as a sidecar file on download

interface HarErrorEntry {
  url: string;
  method: string;
  status: number;             // 0 if the request never completed
  statusText: string;
  trackingid: string | null;
  timeMs: number | null;
  errorBodyExcerpt: string;
  startedDateTime: string;
}
interface LogErrorEntry {
  lineNumber: number;
  level: string;              // ERROR / WARN / FATAL / etc.
  line: string;
  context: string[];          // 2 lines before + 2 lines after
}
interface Attachment {
  id: string;
  name: string;
  kind: 'har' | 'log';
  sizeBytes: number;
  // Summary stats — embedded directly in the markdown brief.
  totalRequests?: number;     // HAR only
  failedRequests?: number;    // HAR only
  totalLines?: number;        // log only
  errorLines?: number;        // log only
  harErrors?: HarErrorEntry[];
  logErrors?: LogErrorEntry[];
  parseError?: string;        // when the file couldn't be parsed
  // Raw text — kept for HARs ≤ HAR_RAW_MD_LIMIT (inlined in .md) and ALWAYS
  // kept for any HAR (emitted as a sidecar file when "Download .md" is hit).
  rawText?: string;
}

interface ScanResult {
  area: AreaKey;
  scannedAt: number;          // epoch ms
  findings: Finding[];
  snapshot: Record<string, unknown>;
  fatalError: string | null;  // a top-level scan failure (network etc.)
}

const AREAS: { key: AreaKey; label: string; subtitle: string }[] = [
  { key: 'wxc',  label: 'WxC',  subtitle: 'Webex Calling — Service App, users, numbers, locations, queues, local gateways' },
  { key: 'wxcc', label: 'WxCC', subtitle: 'Webex Contact Center — queues, entry points, flows, teams, sites, agents' },
  { key: 'cucm', label: 'CUCM', subtitle: 'On-prem CUCM via the tenant spoke — phones, registration, sync recency' },
  { key: 'uccx', label: 'UCCX', subtitle: 'On-prem UCCX via the tenant spoke — credential + spoke reachability' },
  { key: 'lgw',  label: 'LGW',  subtitle: 'CUBE / Local Gateway — gateways, certs, SIP trunks, dial peers, sync' },
];

const STALE_HEARTBEAT_MS = 5 * 60 * 1000;     // 5min: spoke heartbeat is "stale" beyond this
const STALE_SYNC_MS      = 30 * 60 * 1000;    // 30min: D1 last_sync is "stale" beyond this
const EXPIRY_SOON_DAYS   = 30;                // certs/expiries within this window flagged warn

export default function Troubleshooting({ me }: { me: Me }) {
  const [area, setArea] = useState<AreaKey>('wxc');
  // Super-admin tenant override — same pattern as CoreModules: a GLOBAL super-
  // admin needs to pick a real tenant for wxc-svc calls. Persisted locally.
  const [adminTenant, setAdminTenant] = useState<string>(() => {
    const stored = localStorage.getItem('tnh_admin_tenant');
    if (stored != null) return stored;
    return me.tenant_id !== 'GLOBAL' ? me.tenant_id : '';
  });
  useEffect(() => { localStorage.setItem('tnh_admin_tenant', adminTenant); }, [adminTenant]);
  const tenantForCloud = me.role === 'super_admin' && adminTenant ? adminTenant : undefined;
  const effectiveTenantId = (me.role === 'super_admin' ? (adminTenant || me.tenant_id) : me.tenant_id);

  // Per-area scan cache so switching tabs is instant and re-scan is explicit.
  const [scans, setScans] = useState<Partial<Record<AreaKey, ScanResult>>>({});
  const [scanning, setScanning] = useState<Partial<Record<AreaKey, boolean>>>({});
  const [symptom, setSymptom] = useState<string>('');
  const [drilldown, setDrilldown] = useState<Finding | null>(null);
  // Diagnostic attachments — visible in every tab, but the spec calls them out
  // particularly for WxCC Flow Troubleshooting. Shared across tabs so a user
  // can attach once and reuse the analysis when switching between systems.
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);

  const currentScan = scans[area];

  // Auto-run a scan the first time a tab is opened. Subsequent visits reuse the
  // cached result — explicit re-scan is the user's call.
  useEffect(() => {
    if (scans[area] || scanning[area]) return;
    void runScan(area);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area]);

  async function runScan(target: AreaKey) {
    setScanning((s) => ({ ...s, [target]: true }));
    setDrilldown(null);
    try {
      const result = await SCANNERS[target](tenantForCloud, effectiveTenantId);
      setScans((s) => ({ ...s, [target]: result }));
    } finally {
      setScanning((s) => ({ ...s, [target]: false }));
    }
  }

  function openFindingDrilldown(f: Finding) { setDrilldown({ ...f, kind: f.kind ?? 'health-scan' }); }
  function openSymptomDrilldown() {
    const trimmed = symptom.trim();
    if (!trimmed) return;
    setDrilldown({
      id: 'symptom-' + Date.now(),
      severity: 'warn',
      title: trimmed.length > 120 ? trimmed.slice(0, 117) + '…' : trimmed,
      evidence: 'Admin-reported symptom (no automated finding triggered).',
      action: 'Use the live snapshot below to investigate root cause.',
      evidenceData: { symptom_text: trimmed },
      kind: 'symptom',
    });
  }

  async function onAttachFiles(files: FileList | null) {
    setAttachError(null);
    if (!files || files.length === 0) return;
    const next: Attachment[] = [];
    for (const f of Array.from(files)) {
      if (f.size > MAX_ATTACHMENT_BYTES) {
        setAttachError(`"${f.name}" is ${formatBytes(f.size)} — over the ${formatBytes(MAX_ATTACHMENT_BYTES)} cap. Trim or split it before attaching.`);
        continue;
      }
      const lower = f.name.toLowerCase();
      const kind: Attachment['kind'] = lower.endsWith('.har') ? 'har'
                                     : (lower.endsWith('.log') || lower.endsWith('.txt') || lower.endsWith('.json')) ? 'log'
                                     : (await sniffKind(f));
      try {
        const text = await f.text();
        next.push(kind === 'har' ? parseHar(f.name, f.size, text) : parseLog(f.name, f.size, text));
      } catch (e: any) {
        next.push({
          id: 'a-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6),
          name: f.name, kind, sizeBytes: f.size,
          parseError: e?.message ?? String(e),
        });
      }
    }
    if (next.length) setAttachments((cur) => [...cur, ...next]);
  }
  function removeAttachment(id: string) {
    setAttachments((cur) => cur.filter((a) => a.id !== id));
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            <span>Operations</span>
            <span>·</span>
            <span>Cross-system diagnostics</span>
            <span>·</span>
            <span className="font-mono">{effectiveTenantId || 'GLOBAL'}</span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Troubleshooting</h1>
          <p className="text-gray-500 text-sm mt-1 max-w-3xl">
            Live health scan of each system, plus a hand-off builder that packages findings (or your own free-text
            symptom) into a Markdown brief you can drop straight into the TriNetHub Cowork space.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {me.role === 'super_admin' && (
            <div className="flex gap-2 items-center text-sm">
              <span className="text-gray-500">Tenant:</span>
              <input value={adminTenant} onChange={(e) => setAdminTenant(e.target.value)}
                     placeholder="tenant id"
                     className="px-3 py-1.5 text-sm rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent w-40 font-mono" />
            </div>
          )}
        </div>
      </div>

      {/* Sub-nav */}
      <div className="border-b border-tnh-border dark:border-tnh-border-dark overflow-x-auto -mx-2 px-2">
        <div className="flex items-center gap-1 min-w-max">
          {AREAS.map((a) => (
            <button
              key={a.key}
              onClick={() => setArea(a.key)}
              className={`px-3 py-2 -mb-px border-b-2 text-sm whitespace-nowrap transition ${
                a.key === area
                  ? 'border-tnh-accent text-tnh-accent font-medium'
                  : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              {a.label} Troubleshooting
            </button>
          ))}
        </div>
      </div>

      <div className="text-xs text-gray-500 -mt-2">{AREAS.find((a) => a.key === area)?.subtitle}</div>

      {/* Action bar — scan + symptom search */}
      <div className="card p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => runScan(area)}
              disabled={!!scanning[area]}
              className="px-3 py-1.5 rounded-lg bg-tnh-accent text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition"
            >
              {scanning[area] ? 'Scanning…' : (currentScan ? 'Re-scan now' : 'Run health scan')}
            </button>
            {currentScan && (
              <span className="text-xs text-gray-500">
                Last scanned {fmtClock(currentScan.scannedAt)} · {currentScan.findings.length} finding{currentScan.findings.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {drilldown && (
            <button onClick={() => setDrilldown(null)} className="text-xs text-gray-500 hover:underline">Close hand-off panel</button>
          )}
        </div>
        <div className="flex items-stretch gap-2 flex-wrap">
          <input
            value={symptom}
            onChange={(e) => setSymptom(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') openSymptomDrilldown(); }}
            placeholder={`Describe a ${area.toUpperCase()} symptom… (e.g. "outbound calls fail with 503", "an agent can't log in")`}
            className="flex-1 min-w-[20rem] px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm"
          />
          <button
            onClick={openSymptomDrilldown}
            disabled={!symptom.trim()}
            className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-sm font-semibold disabled:opacity-40 hover:opacity-90 transition"
          >
            Build hand-off →
          </button>
        </div>
        <p className="text-[11px] text-gray-500">
          Hand-offs export to your Downloads folder (or your clipboard) — the same folder the TriNetHub Cowork space
          already has access to. There is no live auto-link from this app into a Cowork session.
        </p>
      </div>

      {/* Diagnostic file attachments — HAR + log, parsed client-side. Available
          on every tab; spec calls them out particularly for WxCC. */}
      <AttachmentsBar
        attachments={attachments}
        onPick={onAttachFiles}
        onRemove={removeAttachment}
        error={attachError}
      />

      {/* WxCC Flow Troubleshooting — three search tables (per BUILD-TASK §2b),
          only visible on the WxCC sub-tab. */}
      {area === 'wxcc' && (
        <WxccFlowTroubleshooting onAnalyze={(f) => setDrilldown(f)} activeFindingId={drilldown?.id ?? null} />
      )}

      {/* WxC Call Troubleshooting — Webex Calling Detailed Call History search
          modelled on Control Hub's "Meetings & Calls" view (per §2d). Only
          visible on the WxC sub-tab. */}
      {area === 'wxc' && (
        <WxcCallTroubleshooting
          tenantForCloud={tenantForCloud}
          onAnalyze={(f) => setDrilldown(f)}
          activeFindingId={drilldown?.id ?? null}
        />
      )}

      {/* Findings list — left ; drill-down panel — right */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-3">
          {scanning[area] && !currentScan && <ScanSkeleton />}

          {currentScan?.fatalError && (
            <div className="card p-3 border-rose-300 bg-rose-50 dark:bg-rose-900/15 text-sm text-rose-700 dark:text-rose-200">
              Health scan errored: {currentScan.fatalError}
            </div>
          )}

          {currentScan && currentScan.findings.length === 0 && !currentScan.fatalError && (
            <div className="card p-6 text-center text-sm text-gray-500">
              No findings — this system looked clean during the scan. Use the symptom search above if something
              is wrong that the automated checks didn't catch.
            </div>
          )}

          {currentScan && currentScan.findings.map((f) => (
            <button
              key={f.id}
              onClick={() => openFindingDrilldown(f)}
              className={`w-full text-left card p-4 hover:border-tnh-accent transition ${drilldown?.id === f.id ? 'border-tnh-accent ring-1 ring-tnh-accent/40' : ''}`}
            >
              <div className="flex items-start gap-3">
                <SeverityPill severity={f.severity} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{f.title}</div>
                  <div className="text-xs text-gray-500 mt-1">{f.evidence}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-300 mt-1.5"><span className="font-semibold">Action:</span> {f.action}</div>
                </div>
                <span className="text-xs text-tnh-accent shrink-0">Build hand-off →</span>
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-1">
          {drilldown ? (
            <HandoffPanel
              area={area}
              finding={drilldown}
              scan={currentScan ?? null}
              me={me}
              effectiveTenantId={effectiveTenantId}
              attachments={attachments}
            />
          ) : (
            <div className="card p-4 text-sm text-gray-500">
              <div className="font-medium text-gray-700 dark:text-gray-200 mb-1">Hand-off builder</div>
              Click any finding (or type a symptom and press <kbd className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-[10px]">Enter</kbd>) to assemble
              a Cowork-ready Markdown package with the live snapshot embedded.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SCANNERS — one per area. Each returns a ScanResult with findings + the raw
// snapshot it captured (so the hand-off panel can embed it without re-fetching).
// Every check uses an EXISTING api.* method — no new endpoints.
// ---------------------------------------------------------------------------
type Scanner = (tenant: string | undefined, effectiveTenantId: string) => Promise<ScanResult>;

const SCANNERS: Record<AreaKey, Scanner> = {
  wxc:  scanWxc,
  wxcc: scanWxcc,
  cucm: scanCucm,
  uccx: scanUccx,
  lgw:  scanLgw,
};

async function scanWxc(tenant?: string): Promise<ScanResult> {
  const findings: Finding[] = [];
  const snap: Record<string, unknown> = {};
  let fatal: string | null = null;
  try {
    const [statusR, usersR, locsR, numsR, queuesR, lgwR, rlR, dpR] = await Promise.allSettled([
      api.wxcStatus(tenant),
      api.wxcUsers(50, tenant),
      api.wxcLocations(tenant),
      api.wxcNumbers(200, tenant),
      api.wxcQueues(tenant),
      api.wxcLocalGateways(tenant),
      api.wxcRouteLists(tenant),
      api.wxcDialPlans(tenant),
    ]);

    const status = settled(statusR);
    snap.status = status;
    if (!status) {
      findings.push(mkFinding('error', 'wxc-svc unreachable', describeReject(statusR), 'Verify wxc-svc deploy + that this user has a tenant scope (super_admin must pick a tenant).'));
    } else if (!(status as WxcStatus).configured) {
      findings.push(mkFinding('error', 'Webex Calling Service App not configured',
        `Missing fields: ${((status as WxcStatus).missing ?? []).join(', ') || '(not reported)'}`,
        'Open Configuration Manager and fill in org_id / client_id / client_secret / refresh_token.',
        { status }));
    } else {
      // Per-endpoint probe health — wxc-svc reports per-system errors. Group
      // them by HTTP-status class so the action matches the cause (a 404 is
      // not an auth problem — historically the scanner always said re-auth).
      const errs = (status as WxcStatus).errors ?? {};
      const broken = Object.entries(errs).filter(([, v]) => v != null) as Array<[string, string]>;
      if (broken.length) {
        for (const f of findingsFromProbeErrors(broken, 'Webex Calling API')) findings.push(f);
      } else {
        findings.push(mkFinding('ok', 'Service App OAuth refreshing cleanly',
          'All probed Webex Calling endpoints returned 2xx in this scan.',
          'No action needed.'));
      }
    }

    const users  = settled<{ users: any[]; total: number }>(usersR);
    const locs   = settled<{ locations: any[]; total: number }>(locsR);
    const nums   = settled<{ numbers: any[]; total: number }>(numsR);
    const queues = settled<{ queues: any[]; total: number }>(queuesR);
    snap.counts = { users: users?.total, locations: locs?.total, numbers: nums?.total, queues: queues?.total };

    if (locs && locs.total === 0)  findings.push(mkFinding('warn', 'No Webex Calling locations defined', 'Locations API returned 0 entries.', 'A WxC org needs at least one location before users can be assigned numbers.', { locations: locs }));
    if (users && users.total === 0) findings.push(mkFinding('warn', 'No Webex Calling users found', 'Users API returned 0 entries.', 'Confirm user licensing in Control Hub — Webex Calling licenses must be assigned.', { users_sample: users.users?.slice(0, 5) }));

    // Unassigned numbers — phoneNumberType undefined OR no owner.
    if (nums?.numbers?.length) {
      const unassigned = nums.numbers.filter((n: any) => !n.owner);
      snap.unassigned_numbers_count = unassigned.length;
      if (unassigned.length > 0) {
        findings.push(mkFinding(unassigned.length > nums.numbers.length / 2 ? 'warn' : 'ok',
          `${unassigned.length} of ${nums.numbers.length} numbers have no owner`,
          `Numbers without owner: ${unassigned.slice(0, 3).map((n: any) => n.phoneNumber ?? n.extension).join(', ')}${unassigned.length > 3 ? '…' : ''}`,
          'Assign or release unassigned DIDs to avoid paying for inventory you do not use.',
          { unassigned_sample: unassigned.slice(0, 10) }));
      }
    }

    // Local gateways health.
    const lgw = settled<{ local_gateways: WxcLocalGateway[]; total: number }>(lgwR);
    if (lgw) {
      snap.local_gateways = lgw.local_gateways;
      const bad = (lgw.local_gateways ?? []).filter((g) => g.device_status && g.device_status !== 'online' && g.device_status !== 'registered');
      if (bad.length) {
        findings.push(mkFinding('error', `${bad.length} local gateway(s) not online`,
          bad.map((g) => `${g.name} (${g.device_status})`).join(' · '),
          'Check premise SBC SIP registration toward Webex Calling and IP reachability.',
          { offline_gateways: bad }));
      }
      const unused = (lgw.local_gateways ?? []).filter((g) => !g.in_use);
      if (unused.length) {
        findings.push(mkFinding('warn', `${unused.length} local gateway(s) configured but not in use`,
          unused.map((g) => g.name).join(', '),
          'Route lists / dial plans may not point at these LGWs. Confirm intent.',
          { unused_gateways: unused }));
      }
    }
    snap.route_lists = settled(rlR);
    snap.dial_plans  = settled(dpR);
  } catch (e: any) {
    fatal = e?.message ?? String(e);
  }
  return { area: 'wxc', scannedAt: Date.now(), findings, snapshot: snap, fatalError: fatal };
}

async function scanWxcc(_tenant?: string): Promise<ScanResult> {
  const findings: Finding[] = [];
  const snap: Record<string, unknown> = {};
  let fatal: string | null = null;
  try {
    const [statusR, queuesR, teamsR, sitesR, agentsR, epnR, flowsR] = await Promise.allSettled([
      api.wxccStatus(),
      api.wxccQueues(),
      api.wxccTeams(),
      api.wxccSites(),
      api.wxccAgents(),
      api.wxccEntryPointNumbers(),
      api.wxccFlows(),
    ]);

    const status = settled<WxccStatus>(statusR);
    snap.status = status;
    if (!status) {
      findings.push(mkFinding('error', 'wxcc-svc unreachable', describeReject(statusR), 'Verify wxcc-svc deploy + that this user/tenant has a Webex CC org configured.'));
    } else if (!status.configured) {
      findings.push(mkFinding('error', 'Webex CC not configured',
        `Missing fields: ${(status.missing ?? []).join(', ') || '(not reported)'}`,
        'Open Configuration Manager and add org_id + api_key for this tenant.', { status }));
    } else {
      const errs = status.errors ?? {};
      const broken = Object.entries(errs).filter(([, v]) => v != null) as Array<[string, string]>;
      if (broken.length) {
        for (const f of findingsFromProbeErrors(broken, 'WxCC API')) findings.push(f);
      } else {
        findings.push(mkFinding('ok', 'Webex CC API probes clean',
          'wxcc-svc reports no per-section errors for this tenant.', 'No action needed.'));
      }
    }

    const queues = settled<{ queues: any[]; total: number }>(queuesR);
    const teams  = settled<{ teams: any[]; total: number }>(teamsR);
    const sites  = settled<{ sites: any[]; total: number }>(sitesR);
    const agents = settled<{ agents: any[]; total: number }>(agentsR);
    snap.counts = { queues: queues?.total, teams: teams?.total, sites: sites?.total, agents: agents?.total };

    if (sites && sites.total === 0)   findings.push(mkFinding('warn', 'No WxCC sites defined', 'Sites API returned 0 entries.', 'A WxCC org needs at least one site for team assignment.', { sites }));
    if (queues && queues.total === 0) findings.push(mkFinding('warn', 'No WxCC queues defined', 'Queues API returned 0 entries.', 'Queues are required for any inbound contact routing.', { queues }));
    if (agents && agents.total === 0) findings.push(mkFinding('warn', 'No WxCC agents provisioned', 'Agents API returned 0 entries.', 'No one will answer calls — verify CC licensing in Control Hub.', { agents }));

    // Inactive queue check.
    if (queues?.queues?.length) {
      const inactive = queues.queues.filter((q: any) => q.active === false);
      if (inactive.length) {
        findings.push(mkFinding('warn', `${inactive.length} WxCC queue(s) marked inactive`,
          inactive.slice(0, 5).map((q: any) => q.name).join(', '),
          'Inactive queues drop new contacts. Re-activate or delete.', { inactive_queues: inactive }));
      }
    }

    // Entry points → flows mapping
    const epn = settled<{ entry_points: WxccEntryPointNumbers[]; total_entry_points: number; summary: any }>(epnR);
    if (epn) {
      snap.entry_point_numbers_summary = epn.summary;
      const noFlow = (epn.entry_points ?? []).filter((e) => !e.flow_id);
      if (noFlow.length) {
        findings.push(mkFinding('error', `${noFlow.length} entry point(s) have no flow mapped`,
          noFlow.slice(0, 5).map((e) => e.name).join(', '),
          'Map a published flow to each entry point or callers will get an error treatment.',
          { entry_points_without_flow: noFlow }));
      }
      const noNumbers = (epn.entry_points ?? []).filter((e) => e.flow_id && e.total === 0);
      if (noNumbers.length) {
        findings.push(mkFinding('warn', `${noNumbers.length} entry point(s) have no dial numbers`,
          noNumbers.slice(0, 5).map((e) => e.name).join(', '),
          'Add a DID or extension to each entry point so external/internal calls can reach it.',
          { entry_points_without_numbers: noNumbers }));
      }
    }

    const flows = settled<any>(flowsR);
    if (flows) snap.flows = { total: flows.total, available: flows.available, in_use_count: flows.in_use_count, mapped_count: flows.mapped_count };
  } catch (e: any) {
    fatal = e?.message ?? String(e);
  }
  return { area: 'wxcc', scannedAt: Date.now(), findings, snapshot: snap, fatalError: fatal };
}

async function scanCucm(_tenant?: string, effectiveTenantId?: string): Promise<ScanResult> {
  const findings: Finding[] = [];
  const snap: Record<string, unknown> = {};
  let fatal: string | null = null;
  try {
    const [spokesR, phonesR, eusersR, linesR, rpR, poolsR, cfgR] = await Promise.allSettled([
      api.spokesList(effectiveTenantId),
      api.cucmPhones({ tenant_id: effectiveTenantId, limit: 200 }),
      api.cucmEndUsers({ tenant_id: effectiveTenantId, limit: 50 }),
      api.cucmLines({ tenant_id: effectiveTenantId, limit: 50 }),
      api.cucmRoutePatterns({ tenant_id: effectiveTenantId, limit: 50 }),
      api.cucmDevicePools({ tenant_id: effectiveTenantId, limit: 50 }),
      api.configList(effectiveTenantId),
    ]);

    // Configuration check
    const cfg = settled<any>(cfgR);
    snap.configuration = cfg?.categories?.cucm ?? null;
    if (!cfg) {
      findings.push(mkFinding('error', 'Configuration Manager unreachable', describeReject(cfgR), 'Verify spoke-svc /api/configuration deploy.'));
    } else {
      const cucmCfg = cfg.categories?.cucm;
      const required = (cucmCfg?.fields ?? []) as Array<{ name: string; configured: boolean }>;
      const missing = required.filter((f) => !f.configured).map((f) => f.name);
      if (missing.length) {
        findings.push(mkFinding('error', 'CUCM credentials incomplete in Configuration Manager',
          `Missing: ${missing.join(', ')}`,
          'Add host / AXL port / AXL user / AXL password under Configuration → CUCM.',
          { missing }));
      } else {
        findings.push(mkFinding('ok', 'CUCM credentials configured',
          'All required AXL fields are present in the tenant vault.', 'No action needed.'));
      }
    }

    // On-prem reachability now flows through the Cloudflare Tunnel connector
    // (cucm-tunnel-svc), not the legacy Docker spoke — so CUCM health is derived
    // from whether the live AXL data probes below succeed, not spoke heartbeat.
    const spokes = settled<{ spokes: SpokeRow[]; count: number }>(spokesR);
    snap.spokes = spokes?.spokes;

    // CUCM data probes
    const phones = settled<any>(phonesR);
    snap.phones_envelope = phones ? { tenant_id: phones.tenant_id, total: phones.total, last_sync: phones.last_sync } : null;
    if (phones) {
      const allPhones: CucmPhone[] = phones.phones ?? [];
      if (phones.total === 0) {
        findings.push(mkFinding('warn', 'CUCM returned no phones', 'The CUCM tunnel query returned 0 phones for this tenant.',
          'Check the CUCM tunnel connector on the Spokes page (it should read reachable).', { last_sync: phones.last_sync }));
      } else {
        const unreg = allPhones.filter((p) => p.registration && p.registration.toLowerCase() !== 'registered' && p.registration.toLowerCase() !== 'reg');
        if (unreg.length) {
          findings.push(mkFinding(unreg.length > 5 ? 'error' : 'warn',
            `${unreg.length} of ${allPhones.length} CUCM phones not registered`,
            unreg.slice(0, 5).map((p) => `${p.name} (${p.registration})`).join(' · '),
            'Investigate device pool / CSS / network reachability for unregistered devices.',
            { unregistered_sample: unreg.slice(0, 15) }));
        }
        // Sync recency
        if (phones.last_sync) {
          const lastMs = phones.last_sync.finished_at; // finished_at is epoch ms (cucm_sync_runs, Date.now())
          if (Date.now() - lastMs > STALE_SYNC_MS) {
            findings.push(mkFinding('warn', 'CUCM sync stale',
              `Last AXL sync was ${minsAgo(lastMs)} min ago — beyond the ${STALE_SYNC_MS / 60000}-min freshness threshold.`,
              'Spoke may be backlogged; check Spokes page for the agent\'s last status.',
              { last_sync: phones.last_sync }));
          }
        }
      }
    } else {
      findings.push(mkFinding('error', 'CUCM phones API unreachable', describeReject(phonesR), 'Check the CUCM tunnel connector (cucm-tunnel-svc) and that this tenant has the cucm module enabled.'));
    }

    snap.counts = {
      end_users:       settled<any>(eusersR)?.total ?? null,
      lines:           settled<any>(linesR)?.total ?? null,
      route_patterns:  settled<any>(rpR)?.total ?? null,
      device_pools:    settled<any>(poolsR)?.total ?? null,
    };
  } catch (e: any) {
    fatal = e?.message ?? String(e);
  }
  return { area: 'cucm', scannedAt: Date.now(), findings, snapshot: snap, fatalError: fatal };
}

async function scanUccx(_tenant?: string, effectiveTenantId?: string): Promise<ScanResult> {
  const findings: Finding[] = [];
  const snap: Record<string, unknown> = {};
  let fatal: string | null = null;
  try {
    const [spokesR, cfgR] = await Promise.allSettled([
      api.spokesList(effectiveTenantId),
      api.configList(effectiveTenantId),
    ]);

    const cfg = settled<any>(cfgR);
    snap.configuration = cfg?.categories?.uccx ?? null;
    if (!cfg) {
      findings.push(mkFinding('error', 'Configuration Manager unreachable', describeReject(cfgR), 'Verify spoke-svc /api/configuration deploy.'));
    } else {
      const uccxCfg = cfg.categories?.uccx;
      const required = (uccxCfg?.fields ?? []) as Array<{ name: string; configured: boolean }>;
      const missing = required.filter((f) => !f.configured).map((f) => f.name);
      if (missing.length === required.length && required.length) {
        findings.push(mkFinding('error', 'UCCX credentials not configured',
          `All ${required.length} UCCX fields are empty in the tenant vault.`,
          'Add host / admin user / admin password under Configuration → UCCX.', { missing }));
      } else if (missing.length) {
        findings.push(mkFinding('warn', 'UCCX credentials incomplete',
          `Missing: ${missing.join(', ')}`,
          'Finish the UCCX credential set in Configuration Manager.', { missing }));
      } else {
        findings.push(mkFinding('ok', 'UCCX credentials configured',
          'All required UCCX admin fields are present in the tenant vault.', 'No action needed.'));
      }
    }

    const spokes = settled<{ spokes: SpokeRow[]; count: number }>(spokesR);
    snap.spokes = spokes?.spokes;
    const spokeFinding = analyzeSpoke(spokes, 'UCCX');
    if (spokeFinding) findings.push(spokeFinding);

    findings.push(mkFinding('warn', 'UCCX live telemetry is not yet wired',
      'The hub currently exposes only credential + spoke checks for UCCX. CSQ / agent / app data lives on the spoke.',
      'For deep UCCX diagnosis, capture the symptom in the hand-off and include UCCX logs from the customer-side spoke directly.',
      { note: 'Phase-2 UCCX REST adapter is the planned source.' }));
  } catch (e: any) {
    fatal = e?.message ?? String(e);
  }
  return { area: 'uccx', scannedAt: Date.now(), findings, snapshot: snap, fatalError: fatal };
}

async function scanLgw(tenant?: string): Promise<ScanResult> {
  const findings: Finding[] = [];
  const snap: Record<string, unknown> = {};
  let fatal: string | null = null;
  try {
    const [gwR, certsR, dpR, stR, syncR, lgwCloudR] = await Promise.allSettled([
      api.lgwGateways(),
      api.lgwCerts(),
      api.lgwDialPeers(),
      api.lgwSipTrunks(),
      api.lgwSyncStatus(),
      api.wxcLocalGateways(tenant),
    ]);

    const gws = settled<{ gateways: LgwGateway[]; total: number }>(gwR);
    snap.gateways = gws?.gateways;
    if (!gws) {
      findings.push(mkFinding('error', 'lgw-svc unreachable', describeReject(gwR), 'Verify lgw-svc deploy.'));
    } else if (gws.total === 0) {
      findings.push(mkFinding('warn', 'No CUBE/LGW gateways registered with the hub',
        'lgw_gateways table is empty.',
        'Add a gateway via the CUBE/LGW module so the cube-svc spoke knows what to scrape.',
        { gateways: gws.gateways }));
    } else {
      const bad = gws.gateways.filter((g) => g.status !== 'reachable' && g.status !== 'unknown');
      if (bad.length) {
        findings.push(mkFinding('error', `${bad.length} gateway(s) not reachable`,
          bad.map((g) => `${g.name} (${g.host}: ${g.status})`).join(' · '),
          'Check SSH reachability from the cube-svc spoke — likely network / credential / IOS XE state.',
          { unreachable_gateways: bad }));
      }
      const stale = gws.gateways.filter((g) => g.last_seen_at && (Date.now() - g.last_seen_at) > STALE_SYNC_MS);
      if (stale.length) {
        findings.push(mkFinding('warn', `${stale.length} gateway(s) have stale telemetry`,
          stale.map((g) => `${g.name} (last seen ${minsAgo(g.last_seen_at!)}m ago)`).join(' · '),
          'cube-svc spoke may have stopped polling — check spoke heartbeat.',
          { stale_gateways: stale }));
      }
    }

    // Certs — expiring + expired
    const certs = settled<{ certs: LgwCert[]; total: number }>(certsR);
    snap.certs = certs?.certs;
    if (certs?.certs?.length) {
      const expired = certs.certs.filter((c) => c.days_until_expiry != null && c.days_until_expiry <= 0);
      const expiring = certs.certs.filter((c) => c.days_until_expiry != null && c.days_until_expiry > 0 && c.days_until_expiry <= EXPIRY_SOON_DAYS);
      if (expired.length) {
        findings.push(mkFinding('error', `${expired.length} certificate(s) expired`,
          expired.map((c) => `${c.cn} on ${c.gateway_name ?? c.gateway_id}`).join(' · '),
          'Calls through TLS-bound dial peers will fail. Stage a renewal in the CUBE/LGW → Certificates tab.',
          { expired }));
      }
      if (expiring.length) {
        findings.push(mkFinding('warn', `${expiring.length} certificate(s) expiring within ${EXPIRY_SOON_DAYS} days`,
          expiring.map((c) => `${c.cn} (${c.days_until_expiry}d)`).join(' · '),
          'Stage a renewal early — TLS dial peers will hard-fail once the not_after passes.',
          { expiring }));
      }
    }

    // SIP trunk state
    const trunks = settled<{ sip_trunks: CubeSipTrunk[]; total: number }>(stR);
    snap.sip_trunks = trunks?.sip_trunks;
    if (trunks?.sip_trunks?.length) {
      const down = trunks.sip_trunks.filter((t) => t.registration_state && t.registration_state.toLowerCase() !== 'registered' && t.registration_state.toLowerCase() !== 'up');
      if (down.length) {
        findings.push(mkFinding('error', `${down.length} SIP trunk(s) not registered/up`,
          down.map((t) => `${t.name} → ${t.registrar} (${t.registration_state})`).join(' · '),
          'Outbound calls via those trunks will fail. Check carrier credentials and IP reachability.',
          { down_trunks: down }));
      }
      const slow = trunks.sip_trunks.filter((t) => t.options_state && t.options_state.toLowerCase() !== 'up');
      if (slow.length) {
        findings.push(mkFinding('warn', `${slow.length} SIP trunk(s) failing SIP OPTIONS ping`,
          slow.map((t) => `${t.name} (${t.options_state})`).join(' · '),
          'Even if registered, an OPTIONS-fail trunk may drop calls. Investigate carrier-side reachability.',
          { options_fail: slow }));
      }
    }

    const dialPeers = settled<{ dial_peers: any[]; total: number }>(dpR);
    snap.dial_peer_count = dialPeers?.total ?? null;
    if (dialPeers && dialPeers.total === 0) {
      findings.push(mkFinding('warn', 'No dial peers synced from CUBE',
        'cube_dial_peers table is empty.',
        'Either the gateway has no dial peers, or the spoke has not yet run show dial-peer voice. Check Spokes page.',
        {}));
    }

    // Sync runs — show last run status
    const sync = settled<{ sync_runs: LgwSyncRun[]; total: number }>(syncR);
    snap.sync = sync;
    if (sync?.sync_runs?.length) {
      const latest = sync.sync_runs[0];
      if (latest.status !== 'ok' && latest.status !== 'success') {
        findings.push(mkFinding('warn', `Latest cube-svc sync ended with status "${latest.status}"`,
          latest.error_detail || `Run ${latest.run_id} · ${latest.rows_received} rows`,
          'Check the cube-svc spoke log; the next sync may self-heal but persistent failures need intervention.',
          { latest_sync: latest }));
      }
    }

    const lgwCloud = settled<{ local_gateways: WxcLocalGateway[]; total: number }>(lgwCloudR);
    snap.lgw_cloud_view = lgwCloud?.local_gateways;
    if (lgwCloud?.local_gateways?.length) {
      const offline = lgwCloud.local_gateways.filter((g) => g.device_status && g.device_status !== 'online' && g.device_status !== 'registered');
      if (offline.length) {
        findings.push(mkFinding('error', `${offline.length} LGW(s) offline from Webex Calling's perspective`,
          offline.map((g) => `${g.name} (${g.device_status})`).join(' · '),
          'Webex Calling sees the LGW as down even if the device is up. Confirm outbound proxy + cloud-side SIP registration.',
          { offline_cloud_view: offline }));
      }
    }
  } catch (e: any) {
    fatal = e?.message ?? String(e);
  }
  return { area: 'lgw', scannedAt: Date.now(), findings, snapshot: snap, fatalError: fatal };
}

// ---------------------------------------------------------------------------
// Hand-off panel — builds the Markdown package and exposes Copy + Download.
// ---------------------------------------------------------------------------
function HandoffPanel({ area, finding, scan, me, effectiveTenantId, attachments }:
  { area: AreaKey; finding: Finding; scan: ScanResult | null; me: Me; effectiveTenantId: string; attachments: Attachment[] }) {

  const markdown = useMemo(() => buildPackage(area, finding, scan, me, effectiveTenantId, attachments), [area, finding, scan, me, effectiveTenantId, attachments]);
  const [copied, setCopied] = useState(false);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Older browsers — fall back to selection.
      try {
        const ta = document.createElement('textarea');
        ta.value = markdown;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch { /* surfaces in the textarea */ }
    }
  }

  function triggerDownload(filename: string, mime: string, body: BlobPart) {
    const blob = new Blob([body], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function onDownload() {
    const base = `trinethub-${area}-${slug(finding.title)}-${stamp(Date.now())}`;
    triggerDownload(`${base}.md`, 'text/markdown;charset=utf-8', markdown);
    // Spec §2c: include the raw file in the Download bundle so Claude can read
    // the full capture from the workspace folder. We emit each HAR (regardless
    // of size) as a sidecar download.
    for (const a of attachments) {
      if (a.kind === 'har' && a.rawText) {
        triggerDownload(`${base}__${a.name}`, 'application/json;charset=utf-8', a.rawText);
      }
    }
  }

  const harCount = attachments.filter((a) => a.kind === 'har').length;

  return (
    <div className="card p-4 space-y-3 sticky top-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Hand-off · {area.toUpperCase()}</div>
          <div className="font-medium text-sm mt-0.5">{finding.title}</div>
        </div>
        <SeverityPill severity={finding.severity} />
      </div>

      <div className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
        Download lands in your <strong>Downloads</strong> folder — the same one the TriNetHub Cowork space already has
        access to. Open the Cowork chat and ask Claude to read the file.
        {harCount > 0 && (
          <>
            {' '}Each attached HAR is saved as a sidecar file in the same Download — keep them in the same folder.
          </>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        <button onClick={onCopy} className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-sm font-semibold hover:opacity-90 transition">
          {copied ? '✓ Copied' : 'Copy to clipboard'}
        </button>
        <button onClick={onDownload} className="px-3 py-1.5 rounded-md bg-gray-200 dark:bg-gray-700 text-sm font-semibold hover:opacity-90 transition">
          Download .md{harCount > 0 ? ` + ${harCount} HAR sidecar${harCount === 1 ? '' : 's'}` : ''}
        </button>
      </div>

      <textarea
        readOnly
        value={markdown}
        className="w-full h-80 px-3 py-2 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-gray-50 dark:bg-gray-900/40 text-[11px] font-mono leading-snug"
      />

      <Link to="/spokes" className="text-[11px] text-tnh-accent hover:underline block">
        Need a deeper on-prem snapshot? Open Spokes page →
      </Link>
    </div>
  );
}

function buildPackage(area: AreaKey, finding: Finding, scan: ScanResult | null, me: Me, effectiveTenantId: string, attachments: Attachment[]): string {
  const lines: string[] = [];
  lines.push(`# Troubleshooting Hand-off — ${area.toUpperCase()}`);
  lines.push('');
  lines.push(`**Generated by:** TriNetHub Troubleshooting module`);
  lines.push(`**User:** ${me.email} (${me.role})`);
  lines.push(`**Tenant:** \`${effectiveTenantId}\``);
  lines.push(`**Generated at:** ${new Date().toISOString()}`);
  if (scan) lines.push(`**Scan captured at:** ${new Date(scan.scannedAt).toISOString()}`);
  if (finding.kind && finding.kind !== 'health-scan') lines.push(`**Hand-off kind:** ${finding.kind}`);
  lines.push('');
  lines.push('## Symptom / Finding');
  lines.push('');
  lines.push(`- **Severity:** ${finding.severity.toUpperCase()}`);
  lines.push(`- **Title:** ${finding.title}`);
  lines.push(`- **Observed evidence:** ${finding.evidence}`);
  lines.push(`- **Suggested next step:** ${finding.action}`);
  if (finding.evidenceData) {
    lines.push('');
    lines.push('### Finding-specific data');
    lines.push('```json');
    lines.push(safeStringify(finding.evidenceData));
    lines.push('```');
  }
  lines.push('');

  if (attachments.length) {
    lines.push('## Attached diagnostic files');
    lines.push('');
    for (const a of attachments) {
      lines.push(`### ${a.kind === 'har' ? 'HAR' : 'Log'} — \`${a.name}\` (${formatBytes(a.sizeBytes)})`);
      lines.push('');
      if (a.parseError) {
        lines.push(`**Parse error:** ${a.parseError}`);
        lines.push('');
        continue;
      }
      if (a.kind === 'har') {
        lines.push(`- Total requests: **${a.totalRequests}**`);
        lines.push(`- Failed (non-2xx / aborted) requests: **${a.failedRequests}**`);
        if (a.harErrors && a.harErrors.length) {
          lines.push('');
          lines.push('| # | Method | Status | URL | trackingid | Time (ms) |');
          lines.push('|---|---|---|---|---|---|');
          a.harErrors.slice(0, 50).forEach((e, i) => {
            const trunc = e.url.length > 80 ? e.url.slice(0, 77) + '…' : e.url;
            lines.push(`| ${i + 1} | ${e.method} | ${e.status || '—'} ${e.statusText ? '(' + e.statusText + ')' : ''} | \`${trunc.replace(/\|/g, '\\|')}\` | ${e.trackingid ? '`' + e.trackingid + '`' : '—'} | ${e.timeMs != null ? Math.round(e.timeMs) : '—'} |`);
          });
          if (a.harErrors.length > 50) lines.push(`| … | _${a.harErrors.length - 50} more_ |  |  |  |  |`);
          lines.push('');
          lines.push('### Failed request response bodies (first 600 chars each)');
          lines.push('');
          a.harErrors.slice(0, 25).forEach((e, i) => {
            if (!e.errorBodyExcerpt) return;
            lines.push(`**#${i + 1} ${e.method} ${e.status} ${e.url}**`);
            lines.push('```');
            lines.push(e.errorBodyExcerpt);
            lines.push('```');
          });
        }
        if (a.rawText && a.sizeBytes <= HAR_RAW_MD_LIMIT) {
          lines.push('### Raw HAR (inlined — file ≤ 64 KB)');
          lines.push('```json');
          lines.push(a.rawText);
          lines.push('```');
        } else if (a.rawText) {
          lines.push(`_(Raw HAR is too large to inline. Saved as a sidecar file: \`<download-stem>__${a.name}\` in the same folder.)_`);
        }
      } else {
        lines.push(`- Total lines: **${a.totalLines}**`);
        lines.push(`- Error / warning lines: **${a.errorLines}**`);
        if (a.logErrors && a.logErrors.length) {
          lines.push('');
          lines.push(`Showing the first ${Math.min(a.logErrors.length, 80)} error/warning hit(s) with 2 lines of context above + below.`);
          lines.push('');
          lines.push('```');
          for (const e of a.logErrors.slice(0, 80)) {
            lines.push(`── line ${e.lineNumber}  [${e.level}] ─────────────────────────`);
            for (const c of e.context) lines.push(c);
          }
          lines.push('```');
        }
      }
      lines.push('');
    }
  }

  lines.push(`## Live snapshot — ${area.toUpperCase()}`);
  lines.push('');
  lines.push('Captured during the same scan that produced this finding. Every value is real production data —');
  lines.push('there is no mock content in this snapshot.');
  lines.push('');
  lines.push('```json');
  lines.push(safeStringify(scan?.snapshot ?? {}));
  lines.push('```');
  lines.push('');
  lines.push(`## All findings from this scan`);
  lines.push('');
  if (scan?.findings.length) {
    for (const f of scan.findings) {
      lines.push(`- **[${f.severity.toUpperCase()}]** ${f.title} — ${f.evidence}`);
    }
  } else {
    lines.push('_(none recorded — the symptom above was supplied by the admin.)_');
  }
  lines.push('');
  lines.push('## Environment');
  lines.push('');
  lines.push(`- TriNetHub SPA: https://trinethub.com`);
  lines.push(`- API base: https://api.trinethub.com`);
  lines.push(`- System area: ${areaFullName(area)}`);
  lines.push(`- Tenant scope: \`${effectiveTenantId}\``);
  lines.push('');
  lines.push('## Analysis prompt for Claude');
  lines.push('');
  lines.push(analysisPrompt(area, finding, attachments));
  lines.push('');
  return lines.join('\n');
}

function analysisPrompt(area: AreaKey, finding: Finding, attachments: Attachment[]): string {
  const persona = {
    wxc:  'You are a senior Webex Calling solutions engineer.',
    wxcc: 'You are a senior Webex Contact Center solutions engineer with Flow Designer, Search/Task API, and routing depth.',
    cucm: 'You are a senior Cisco UC engineer with deep CUCM AXL / device-pool / CSS knowledge.',
    uccx: 'You are a senior Cisco UCCX engineer with script, CSQ, and Informix DB depth.',
    lgw:  'You are a senior Cisco voice / CUBE / SIP-trunking engineer with IOS XE depth.',
  }[area];

  const harNote = attachments.some((a) => a.kind === 'har')
    ? '  - **Attached HAR**: the failed requests table (URL, method, status, trackingid, timing) and the truncated response bodies are above. For HARs larger than 64 KB the full file is saved as a sidecar — read it from the same folder when you need the complete capture.'
    : '';
  const logNote = attachments.some((a) => a.kind === 'log')
    ? '  - **Attached logs**: ERROR / WARN hits with surrounding context lines are above.'
    : '';
  const attachContext = (harNote || logNote) ? `\n\nAttached diagnostic files (parsed CLIENT-SIDE):\n${[harNote, logNote].filter(Boolean).join('\n')}` : '';

  const isActionItems = finding.analysisMode === 'action-items';
  const job = isActionItems
    ? [
        'Your job — analyse the errors against WxCC backend behaviour and return the root cause AS ACTION ITEMS the admin can execute today. Output ONLY the two sections below, no preamble:',
        '',
        '## Root cause',
        'One paragraph. Be concrete — point at the specific API, scope, flow node, queue, entry point, or routing rule that is wrong.',
        '',
        '## Action items',
        'A numbered list. Each item starts with a verb (e.g. "Re-publish flow `Main_IVR_v3`", "Add scope `cjp:cms_read` in Control Hub", "Re-map dial number 5625706100 to entry point `Sales_EP`"). Include the exact place (Control Hub path / Configuration Manager field / `wxcc-svc` endpoint) the admin should perform the action.',
        '',
        'If the live snapshot or the attached files are insufficient to be that concrete, the last action item must be exactly what additional capture to gather, named: "Attach [specific thing] then re-run the hand-off."',
      ].join('\n')
    : [
        'Your job:',
        '  1. State the most likely root cause in one paragraph.',
        '  2. List the next 3-5 diagnostic steps in order — CLI / API / Control Hub / Spoke / Configuration Manager.',
        '  3. Call out anything in the snapshot that contradicts your hypothesis.',
        '  4. If the data is insufficient, name exactly which additional command / endpoint output the admin should attach.',
      ].join('\n');

  return [
    persona,
    '',
    'You are reading a TriNetHub Troubleshooting hand-off. Above this prompt you have:',
    '  1. The specific finding (symptom + observed evidence + suggested next step).',
    '  2. A JSON snapshot of the relevant live data captured at the moment of the scan.',
    '  3. The full list of findings produced by the same scan.' + attachContext,
    '',
    job,
    '',
    `Finding title for quick reference: "${finding.title}"`,
  ].join('\n');
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
function mkFinding(severity: Severity, title: string, evidence: string, action: string, evidenceData?: unknown): Finding {
  return { id: `${severity}-${slug(title)}-${Math.random().toString(36).slice(2, 7)}`, severity, title, evidence, action, evidenceData };
}

// Classify a wxc-svc / wxcc-svc probe error string into an HTTP-status class so
// the health scan can give the admin a *correct* action — a 404 is not an auth
// problem, and historically the scanner always suggested re-auth which is
// misleading. The error strings these services throw are
// `webex_api_${status}: ${body}` / `wxcc_api_${status}: ${body}`, so we parse
// the status code out of the message.
type ProbeErrClass = 'auth' | 'not_found' | 'cisco_5xx' | 'other';
function classifyProbeError(message: string): { cls: ProbeErrClass; status: number | null } {
  if (!message) return { cls: 'other', status: null };
  // matches `webex_api_404:`, `wxcc_api_500:`, `*_api_4xx`, etc.
  const m = message.match(/\bapi_(\d{3})\b/);
  const status = m ? Number(m[1]) : null;
  if (status === 401 || status === 403) return { cls: 'auth', status };
  if (status === 404)                   return { cls: 'not_found', status };
  if (status != null && status >= 500 && status < 600) return { cls: 'cisco_5xx', status };
  return { cls: 'other', status };
}
const PROBE_CLASS_ACTION: Record<ProbeErrClass, { label: string; action: string }> = {
  auth:      { label: 'auth (401/403)',     action: 'Re-authorize the Service App in Configuration Manager — refresh token is expired, revoked, or missing the scope.' },
  not_found: { label: 'endpoint not found (404)', action: 'Endpoint not found — a wrong API path or a resource that does not exist. NOT an auth problem. Verify the path; if it changed in Webex, file an issue to update wxc-svc / wxcc-svc.' },
  cisco_5xx: { label: 'Webex-side 5xx',     action: 'Webex-side error — retry the scan. If it persists, check the Webex Calling status page (status.webex.com).' },
  other:     { label: 'network / other',    action: 'Network or other failure — confirm the worker can reach webexapis.com / api.wxcc-*.cisco.com. Check the Cloudflare worker dashboard for the failing request.' },
};

// Group a {endpoint → error string} map into one Finding per HTTP-status class,
// so a 404 on /queues doesn't tell the admin to re-auth.
function findingsFromProbeErrors(broken: Array<[string, string]>, svcLabel: string): Finding[] {
  if (!broken.length) return [];
  const byClass: Record<ProbeErrClass, Array<{ ep: string; err: string; status: number | null }>> = {
    auth: [], not_found: [], cisco_5xx: [], other: [],
  };
  for (const [ep, err] of broken) {
    const { cls, status } = classifyProbeError(err);
    byClass[cls].push({ ep, err, status });
  }
  const out: Finding[] = [];
  (['auth', 'cisco_5xx', 'not_found', 'other'] as ProbeErrClass[]).forEach((cls) => {
    const hits = byClass[cls];
    if (!hits.length) return;
    const meta = PROBE_CLASS_ACTION[cls];
    const sev: Severity = cls === 'not_found' ? 'warn' : 'error';
    const epSummary = hits.map((h) => `${h.ep}${h.status != null ? ` (${h.status})` : ''}`).join(', ');
    out.push(mkFinding(sev,
      `${hits.length} ${svcLabel} probe${hits.length === 1 ? '' : 's'} — ${meta.label}`,
      `Affected: ${epSummary}. Sample: ${(hits[0]?.err ?? '').slice(0, 140)}`,
      meta.action,
      { class: cls, hits }));
  });
  return out;
}

function analyzeSpoke(spokes: { spokes: SpokeRow[]; count: number } | null, label: string): Finding | null {
  if (!spokes) {
    return mkFinding('error', `Spoke list unreachable for ${label}`, 'spoke-svc /api/spokes returned no data.',
      'Verify spoke-svc deploy and that this tenant has at least one bootstrap.');
  }
  if (spokes.count === 0) {
    return mkFinding('error', `No spoke registered for this tenant`,
      `${label} relies on the on-prem spoke; none is bootstrapped yet.`,
      'Go to Spokes → bootstrap a new install link, then run the one-liner on the customer-side host.',
      { count: spokes.count });
  }
  // Pick the freshest spoke.
  const fresh = [...spokes.spokes].sort((a, b) => (b.last_heartbeat_at ?? 0) - (a.last_heartbeat_at ?? 0))[0];
  if (!fresh?.last_heartbeat_at) {
    return mkFinding('error', `Spoke registered but never heartbeat`,
      `${spokes.count} spoke(s) bootstrapped, none have phoned home yet.`,
      'Confirm the spoke container is running and outbound 443 to api.trinethub.com is allowed.',
      { spokes: spokes.spokes });
  }
  const ageMs = Date.now() - fresh.last_heartbeat_at; // last_heartbeat_at is epoch ms
  if (ageMs > STALE_HEARTBEAT_MS) {
    return mkFinding('warn', `Spoke heartbeat stale`,
      `Freshest spoke ${fresh.spoke_id} last reported ${minsAgo(fresh.last_heartbeat_at)} min ago.`,
      'Check the spoke host; the agent may be stopped or network-isolated.',
      { spoke: fresh, age_ms: ageMs });
  }
  return mkFinding('ok', `Spoke healthy for ${label}`,
    `Latest heartbeat ${minsAgo(fresh.last_heartbeat_at)} min ago from ${fresh.spoke_id}.`,
    'No action needed.');
}

function settled<T>(r: PromiseSettledResult<T>): T | null {
  return r.status === 'fulfilled' ? r.value : null;
}
function describeReject(r: PromiseSettledResult<unknown>): string {
  if (r.status === 'rejected') return r.reason instanceof Error ? r.reason.message : String(r.reason);
  return '(unknown — fulfilled but parsed as null)';
}

function safeStringify(v: unknown): string {
  try { return JSON.stringify(v, null, 2); }
  catch { return String(v); }
}
function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
}
function stamp(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}
function fmtClock(ms: number): string {
  return new Date(ms).toLocaleTimeString();
}
function minsAgo(ms: number): number { return Math.round((Date.now() - ms) / 60000); }

function areaFullName(a: AreaKey): string {
  return ({
    wxc:  'Webex Calling (wxc-svc)',
    wxcc: 'Webex Contact Center (wxcc-svc)',
    cucm: 'CUCM via on-prem spoke (spoke-svc)',
    uccx: 'UCCX via on-prem spoke (spoke-svc)',
    lgw:  'CUBE / Local Gateway (lgw-svc + cube-svc spoke)',
  })[a];
}

function SeverityPill({ severity }: { severity: Severity }) {
  const map = {
    ok:    { bg: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', dot: 'bg-emerald-500', label: 'OK' },
    warn:  { bg: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',           dot: 'bg-amber-500',   label: 'WARN' },
    error: { bg: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',               dot: 'bg-rose-500',    label: 'ERROR' },
  } as const;
  const m = map[severity];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} /> {m.label}
    </span>
  );
}

function ScanSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="card p-4 animate-pulse">
          <div className="h-3 w-32 rounded bg-gray-200 dark:bg-gray-700 mb-2" />
          <div className="h-3 w-2/3 rounded bg-gray-200 dark:bg-gray-700 mb-1" />
          <div className="h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AttachmentsBar — drop / pick HAR + log files for any sub-tab. Parsing is
// client-side; the parsed summary feeds the hand-off package.
// ---------------------------------------------------------------------------
function AttachmentsBar({ attachments, onPick, onRemove, error }:
  { attachments: Attachment[]; onPick: (files: FileList | null) => void; onRemove: (id: string) => void; error: string | null }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  return (
    <div
      className={`card p-4 space-y-3 ${dragging ? 'border-tnh-accent ring-1 ring-tnh-accent/30' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); onPick(e.dataTransfer.files); }}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold">Attach diagnostic files</div>
          <div className="text-[11px] text-gray-500">
            HAR (browser HTTP Archive) and plain-text logs (.log / .txt / .json). Parsed in the browser — max {formatBytes(MAX_ATTACHMENT_BYTES)} per file.
            The findings (failed requests, error lines) are embedded into the hand-off; HARs are also saved as sidecar files when you download.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => inputRef.current?.click()}
            className="px-3 py-1.5 rounded-md bg-gray-200 dark:bg-gray-700 text-sm font-semibold hover:opacity-90 transition"
          >
            + Add files
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".har,.log,.txt,.json,application/json,text/plain"
            className="hidden"
            onChange={(e) => { onPick(e.target.files); if (inputRef.current) inputRef.current.value = ''; }}
          />
        </div>
      </div>
      {error && <div className="text-xs text-rose-600 dark:text-rose-300">{error}</div>}
      {attachments.length > 0 && (
        <ul className="space-y-1.5">
          {attachments.map((a) => (
            <li key={a.id} className="flex items-center gap-3 text-xs px-2 py-1.5 rounded-md bg-gray-50 dark:bg-gray-900/40 border border-tnh-border dark:border-tnh-border-dark">
              <span className="font-mono uppercase text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700">{a.kind}</span>
              <span className="font-medium truncate flex-1">{a.name}</span>
              <span className="text-gray-500 whitespace-nowrap">{formatBytes(a.sizeBytes)}</span>
              {a.parseError
                ? <span className="text-rose-600 dark:text-rose-300 truncate">parse error: {a.parseError}</span>
                : a.kind === 'har'
                  ? <span className="text-gray-600 dark:text-gray-300 whitespace-nowrap">{a.failedRequests ?? 0} failed / {a.totalRequests ?? 0} total</span>
                  : <span className="text-gray-600 dark:text-gray-300 whitespace-nowrap">{a.errorLines ?? 0} err+warn / {a.totalLines ?? 0} lines</span>
              }
              <button onClick={() => onRemove(a.id)} className="text-rose-500 hover:underline">remove</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// WxccFlowTroubleshooting — three search tables per BUILD-TASK §2b.
//
//   A. Search by Interaction ID — wxccInteractionLookup() — probes Cisco's
//      Task / Search API paths and returns the first 2xx hit (or a found:false
//      probe trail). Result → "Analyze with Claude" → action-items handoff.
//
//   B. Capture by Tracking ID — no backend lookup (Tracking ID is Cisco-internal,
//      not queryable by a customer Service App). Bundles the Tracking ID + the
//      agent's error text with the WxCC context and labels the ID as the
//      "value to give Cisco TAC".
//
//   C. Search by ANI / DNIS — wxccInteractionsByNumber() — probes number-filter
//      paths. Each result row → "Analyze with Claude".
// ---------------------------------------------------------------------------
function WxccFlowTroubleshooting({ onAnalyze, activeFindingId }: { onAnalyze: (f: Finding) => void; activeFindingId: string | null }) {
  // ── A. Interaction ID ──────────────────────────────────────────────────
  const [interactionId, setInteractionId] = useState('');
  const [interactionResult, setInteractionResult] = useState<WxccInteractionLookupResponse | null>(null);
  const [interactionLoading, setInteractionLoading] = useState(false);
  const [interactionError, setInteractionError] = useState<string | null>(null);

  async function runInteractionLookup() {
    const id = interactionId.trim();
    if (!id) return;
    setInteractionLoading(true); setInteractionResult(null); setInteractionError(null);
    try {
      const r = await api.wxccInteractionLookup(id);
      setInteractionResult(r);
    } catch (e: any) {
      setInteractionError(e?.message ?? String(e));
    } finally {
      setInteractionLoading(false);
    }
  }

  function handoffInteraction() {
    if (!interactionResult) return;
    const id = interactionResult.interaction_id;
    const sev: Severity = interactionResult.found ? 'warn' : 'error';
    const title = interactionResult.found
      ? `Interaction ${id} — analyse errors / events`
      : `Interaction ${id} — WxCC Search API not reachable for this Service App`;
    const evidence = interactionResult.found
      ? `Probe used: ${interactionResult.probe_used}. Interaction payload + (best-effort) events captured.`
      : `${interactionResult.probes.length} candidate WxCC Task/Search paths probed; none returned 2xx.`;
    onAnalyze({
      id: `wxcc-interaction-${id}-${Date.now()}`,
      severity: sev,
      title,
      evidence,
      action: 'Analyze with Claude — return root cause as action items.',
      evidenceData: {
        interaction_id: id,
        found: interactionResult.found,
        probe_used: interactionResult.probe_used ?? null,
        interaction: interactionResult.interaction ?? null,
        events: interactionResult.events ?? null,
        probes: interactionResult.probes,
        wxcc_svc_message: interactionResult.message ?? null,
      },
      kind: 'wxcc-interaction',
      analysisMode: 'action-items',
    });
  }

  // ── B. Tracking ID ─────────────────────────────────────────────────────
  const [trackingId, setTrackingId] = useState('');
  const [agentError, setAgentError] = useState('');

  function handoffTracking() {
    const tid = trackingId.trim();
    const err = agentError.trim();
    if (!tid && !err) return;
    onAnalyze({
      id: `wxcc-tracking-${Date.now()}`,
      severity: 'warn',
      title: tid ? `Tracking ID ${tid} — ${err.slice(0, 80) || 'admin-captured WxCC error'}` : 'WxCC error capture',
      evidence: tid
        ? `Tracking ID is Cisco-internal — NOT customer-queryable. It is the value to give Cisco TAC.`
        : 'No Tracking ID supplied — agent-reported error only.',
      action: 'Analyze with Claude — return root cause as action items. If the only fix is a TAC case, label the Tracking ID as the value to attach to the case.',
      evidenceData: {
        tracking_id: tid || null,
        tracking_id_note: 'WxCC Tracking ID is the Cisco-internal request correlation ID. Customer Service Apps cannot look it up — Cisco TAC reads it from their internal logs.',
        agent_error_text: err || null,
      },
      kind: 'wxcc-tracking',
      analysisMode: 'action-items',
    });
  }

  // ── C. ANI / DNIS ──────────────────────────────────────────────────────
  const [callingFrom, setCallingFrom] = useState('');
  const [calledTo, setCalledTo] = useState('');
  const [numberResult, setNumberResult] = useState<WxccInteractionsByNumberResponse | null>(null);
  const [numberLoading, setNumberLoading] = useState(false);
  const [numberError, setNumberError] = useState<string | null>(null);

  async function runNumberSearch() {
    const from = callingFrom.trim();
    const to = calledTo.trim();
    if (!from && !to) return;
    setNumberLoading(true); setNumberResult(null); setNumberError(null);
    try {
      const r = await api.wxccInteractionsByNumber({ from, to });
      setNumberResult(r);
    } catch (e: any) {
      setNumberError(e?.message ?? String(e));
    } finally {
      setNumberLoading(false);
    }
  }

  function handoffNumber(matchIndex: number | null) {
    if (!numberResult) return;
    const match = matchIndex != null && numberResult.interactions ? numberResult.interactions[matchIndex] : null;
    const title = numberResult.found
      ? match
        ? `Interaction from ${numberResult.filter.from || '—'} → ${numberResult.filter.to || '—'} (#${(matchIndex ?? 0) + 1})`
        : `Number-filtered interactions: ${numberResult.filter.from || '—'} → ${numberResult.filter.to || '—'} (${numberResult.total ?? 0})`
      : `No reachable WxCC interactions for ${numberResult.filter.from || '—'} → ${numberResult.filter.to || '—'}`;
    onAnalyze({
      id: `wxcc-number-${Date.now()}-${matchIndex ?? 'all'}`,
      severity: numberResult.found ? 'warn' : 'error',
      title,
      evidence: numberResult.found
        ? `Probe used: ${numberResult.probe_used}. ${numberResult.total ?? 0} interaction(s) matched.`
        : `${numberResult.probes.length} candidate paths probed; none returned 2xx.`,
      action: 'Analyze with Claude — return root cause as action items.',
      evidenceData: {
        filter: numberResult.filter,
        found: numberResult.found,
        probe_used: numberResult.probe_used ?? null,
        total: numberResult.total ?? null,
        matched_interaction: match ?? null,
        all_interactions: matchIndex == null ? numberResult.interactions ?? null : undefined,
        probes: numberResult.probes,
        wxcc_svc_message: numberResult.message ?? null,
      },
      kind: 'wxcc-number',
      analysisMode: 'action-items',
    });
  }

  return (
    <div className="card p-4 space-y-5">
      <div>
        <div className="text-sm font-semibold">WxCC Flow Troubleshooting</div>
        <div className="text-[11px] text-gray-500">
          Live lookups against Cisco's WxCC API plus a Tracking-ID capture for Cisco TAC. Every search builds an
          "Analyze with Claude" hand-off that asks for the root cause <em>as numbered action items</em>.
        </div>
      </div>

      {/* ── A. Search by Interaction ID ─────────────────────────────────── */}
      <section className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">A. Search by Interaction ID</div>
        <div className="flex items-stretch gap-2 flex-wrap">
          <input
            value={interactionId}
            onChange={(e) => setInteractionId(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void runInteractionLookup(); }}
            placeholder="e.g. 8e0f4a35-…  (WxCC Interaction ID)"
            className="flex-1 min-w-[18rem] px-3 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono"
          />
          <button
            onClick={runInteractionLookup}
            disabled={interactionLoading || !interactionId.trim()}
            className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-sm font-semibold hover:opacity-90 disabled:opacity-40 transition"
          >
            {interactionLoading ? 'Probing…' : 'Search'}
          </button>
        </div>
        {interactionError && <div className="text-xs text-rose-600 dark:text-rose-300">{interactionError}</div>}
        {interactionResult && (
          <InteractionResultTable
            result={interactionResult}
            onAnalyze={handoffInteraction}
            activeFindingId={activeFindingId}
          />
        )}
      </section>

      {/* ── B. Capture by Tracking ID ───────────────────────────────────── */}
      <section className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">B. Capture by Tracking ID (Cisco-internal — for TAC)</div>
        <div className="grid gap-2 sm:grid-cols-2">
          <input
            value={trackingId}
            onChange={(e) => setTrackingId(e.target.value)}
            placeholder="Tracking ID (from agent toast / browser response header)"
            className="px-3 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono"
          />
          <input
            value={agentError}
            onChange={(e) => setAgentError(e.target.value)}
            placeholder='Agent-reported error (e.g. "Failed to submit wrap up")'
            className="px-3 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handoffTracking}
            disabled={!trackingId.trim() && !agentError.trim()}
            className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-sm font-semibold hover:opacity-90 disabled:opacity-40 transition"
          >
            Analyze with Claude →
          </button>
          <span className="text-[11px] text-gray-500">
            No backend lookup — Tracking IDs are read-only to Cisco TAC. The hand-off bundles the ID + your error with
            the current WxCC context.
          </span>
        </div>
      </section>

      {/* ── C. Search by phone number ───────────────────────────────────── */}
      <section className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">C. Search by phone number — Calling From / Called To</div>
        <div className="grid gap-2 sm:grid-cols-[1fr,1fr,auto]">
          <input
            value={callingFrom}
            onChange={(e) => setCallingFrom(e.target.value)}
            placeholder="Calling From / ANI (e.g. +15625709994)"
            className="px-3 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono"
          />
          <input
            value={calledTo}
            onChange={(e) => setCalledTo(e.target.value)}
            placeholder="Called To / DNIS (e.g. +15625706100)"
            className="px-3 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono"
          />
          <button
            onClick={runNumberSearch}
            disabled={numberLoading || (!callingFrom.trim() && !calledTo.trim())}
            className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-sm font-semibold hover:opacity-90 disabled:opacity-40 transition"
          >
            {numberLoading ? 'Probing…' : 'Search'}
          </button>
        </div>
        {numberError && <div className="text-xs text-rose-600 dark:text-rose-300">{numberError}</div>}
        {numberResult && (
          <NumberSearchResultTable
            result={numberResult}
            onAnalyzeAll={() => handoffNumber(null)}
            onAnalyzeRow={(i) => handoffNumber(i)}
            activeFindingId={activeFindingId}
          />
        )}
      </section>
    </div>
  );
}

function InteractionResultTable({ result, onAnalyze, activeFindingId }: { result: WxccInteractionLookupResponse; onAnalyze: () => void; activeFindingId: string | null }) {
  const buttonActive = activeFindingId?.startsWith(`wxcc-interaction-${result.interaction_id}`);
  return (
    <div className="border border-tnh-border dark:border-tnh-border-dark rounded-md overflow-hidden">
      <div className="px-3 py-2 flex items-center justify-between gap-3 flex-wrap bg-gray-50 dark:bg-gray-900/40">
        <div className="text-xs">
          {result.found
            ? <span className="text-emerald-700 dark:text-emerald-300 font-medium">Match · probe used <code className="font-mono">{result.probe_used}</code></span>
            : <span className="text-rose-700 dark:text-rose-300 font-medium">No reachable Task/Search endpoint for this ID</span>}
        </div>
        <button
          onClick={onAnalyze}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${buttonActive ? 'bg-tnh-accent/20 text-tnh-accent' : 'bg-tnh-accent text-white hover:opacity-90'}`}
        >
          Analyze with Claude →
        </button>
      </div>

      {result.found && result.interaction ? (
        <div className="px-3 py-2 space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Interaction payload</div>
          <pre className="text-[11px] font-mono whitespace-pre-wrap bg-gray-50 dark:bg-gray-900/40 p-2 rounded max-h-56 overflow-auto">{safeStringify(result.interaction)}</pre>
          {result.events != null && (
            <>
              <div className="text-[11px] uppercase tracking-wider text-gray-500">Events / activity (best-effort)</div>
              <pre className="text-[11px] font-mono whitespace-pre-wrap bg-gray-50 dark:bg-gray-900/40 p-2 rounded max-h-56 overflow-auto">{safeStringify(result.events)}</pre>
            </>
          )}
        </div>
      ) : (
        <div className="px-3 py-2 space-y-2">
          {result.message && <div className="text-xs text-gray-700 dark:text-gray-300">{result.message}</div>}
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Probes</div>
          <ProbeTrail probes={result.probes} />
        </div>
      )}
    </div>
  );
}

function NumberSearchResultTable({ result, onAnalyzeAll, onAnalyzeRow, activeFindingId }:
  { result: WxccInteractionsByNumberResponse; onAnalyzeAll: () => void; onAnalyzeRow: (i: number) => void; activeFindingId: string | null }) {
  const rows = (result.interactions ?? []) as Array<Record<string, any>>;
  return (
    <div className="border border-tnh-border dark:border-tnh-border-dark rounded-md overflow-hidden">
      <div className="px-3 py-2 flex items-center justify-between gap-3 flex-wrap bg-gray-50 dark:bg-gray-900/40">
        <div className="text-xs">
          {result.found
            ? <span className="text-emerald-700 dark:text-emerald-300 font-medium">{result.total ?? 0} match(es) · probe used <code className="font-mono">{result.probe_used}</code></span>
            : <span className="text-rose-700 dark:text-rose-300 font-medium">No reachable WxCC interaction search for this filter</span>}
        </div>
        {result.found && rows.length > 0 && (
          <button onClick={onAnalyzeAll} className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-xs font-semibold hover:opacity-90 transition">
            Analyze all with Claude →
          </button>
        )}
      </div>

      {result.found && rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-y border-tnh-border dark:border-tnh-border-dark">
              <tr>
                {['#', 'ID', 'ANI', 'DNIS', 'Status', 'Start', '', ''].map((c) => (
                  <th key={c} className="text-left px-3 py-1.5 font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 50).map((r, i) => {
                const isActive = activeFindingId?.includes(`wxcc-number-`) && activeFindingId?.endsWith(`-${i}`);
                return (
                  <tr key={i} className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/40">
                    <td className="px-3 py-1.5 text-gray-500">{i + 1}</td>
                    <td className="px-3 py-1.5 font-mono">{pickField(r, ['id', 'taskId', 'contactId', 'interactionId']) ?? '—'}</td>
                    <td className="px-3 py-1.5 font-mono">{pickField(r, ['ani', 'callingNumber', 'from']) ?? '—'}</td>
                    <td className="px-3 py-1.5 font-mono">{pickField(r, ['dnis', 'calledNumber', 'to']) ?? '—'}</td>
                    <td className="px-3 py-1.5">{pickField(r, ['status', 'state', 'taskStatus']) ?? '—'}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{pickField(r, ['startedDateTime', 'startTime', 'createdAt', 'createdTime']) ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right">
                      <button onClick={() => onAnalyzeRow(i)} className={`px-2 py-1 rounded text-[11px] font-semibold transition ${isActive ? 'bg-tnh-accent/20 text-tnh-accent' : 'bg-tnh-accent text-white hover:opacity-90'}`}>
                        Analyze →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length > 50 && <div className="px-3 py-1.5 text-[11px] text-gray-500 border-t border-tnh-border dark:border-tnh-border-dark">… and {rows.length - 50} more rows hidden. Use "Analyze all" to ship the full set.</div>}
        </div>
      ) : (
        <div className="px-3 py-2 space-y-2">
          {result.message && <div className="text-xs text-gray-700 dark:text-gray-300">{result.message}</div>}
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Probes</div>
          <ProbeTrail probes={result.probes} />
        </div>
      )}
    </div>
  );
}

function ProbeTrail({ probes }: { probes: Array<{ path: string; status: number; body_excerpt?: string }> }) {
  return (
    <ol className="text-[11px] font-mono space-y-0.5 max-h-40 overflow-auto">
      {probes.map((p, i) => (
        <li key={i} className={p.status >= 200 && p.status < 300 ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-600 dark:text-gray-400'}>
          [{String(p.status).padStart(3)}] {p.path}
        </li>
      ))}
    </ol>
  );
}

function pickField(obj: Record<string, any>, names: string[]): string | null {
  for (const n of names) {
    const v = obj?.[n];
    if (v != null && v !== '') return typeof v === 'object' ? safeStringify(v).slice(0, 60) : String(v);
  }
  return null;
}

// ---------------------------------------------------------------------------
// WxcCallTroubleshooting — Webex Calling "Meetings & Calls" -style search
// (BUILD-TASK-troubleshooting §2d). Calling From / Called To + date range,
// results table, click row → call-detail panel with Download + "Analyze
// with Claude" hand-off.
//
// The wxc-svc /api/wxc/calls endpoint PROBES the Detailed Call History API
// across several plausible hosts/paths. If nothing returns 2xx, it surfaces
// the probe trail and we render that plainly instead of fabricating rows.
// ---------------------------------------------------------------------------
function WxcCallTroubleshooting({ tenantForCloud, onAnalyze, activeFindingId }:
  { tenantForCloud: string | undefined; onAnalyze: (f: Finding) => void; activeFindingId: string | null }) {
  const [callingFrom, setCallingFrom] = useState('');
  const [calledTo, setCalledTo]       = useState('');
  // Default the date range to the last 24h — same window wxc-svc would pick.
  const defaultEnd   = useMemo(() => isoLocalDatetime(Date.now()), []);
  const defaultStart = useMemo(() => isoLocalDatetime(Date.now() - 24 * 60 * 60 * 1000), []);
  const [fromDate, setFromDate] = useState<string>(defaultStart);
  const [toDate,   setToDate]   = useState<string>(defaultEnd);
  const [result, setResult]     = useState<WxcCallsResponse | null>(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  // index into result.calls — when set, the right-side call-detail panel opens.
  const [openCallIdx, setOpenCallIdx] = useState<number | null>(null);

  async function runSearch() {
    const f = callingFrom.trim(), t = calledTo.trim();
    if (!f && !t) return;
    setLoading(true); setResult(null); setError(null); setOpenCallIdx(null);
    try {
      const r = await api.wxcCalls({
        from: f || undefined,
        to:   t || undefined,
        from_date: fromDate ? new Date(fromDate).toISOString() : undefined,
        to_date:   toDate   ? new Date(toDate).toISOString()   : undefined,
        tenant: tenantForCloud,
      });
      setResult(r);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }

  // Build a "match summary" handoff for the whole result set.
  function handoffAll() {
    if (!result) return;
    const title = result.found
      ? `WxC calls: ${result.filter.from || '—'} → ${result.filter.to || '—'} (${result.total ?? 0} matched)`
      : `No reachable WxC Detailed Call History for ${result.filter.from || '—'} → ${result.filter.to || '—'}`;
    onAnalyze({
      id: `wxc-call-${Date.now()}`,
      severity: result.found ? 'warn' : 'error',
      title,
      evidence: result.found
        ? `Probe used: ${result.probe_used}. ${result.total ?? 0} matched (of ${result.total_before_filter ?? '—'} returned).`
        : `${result.probes.length} candidate CDR paths probed; none returned 2xx.`,
      action: 'Analyze with Claude — return root cause as action items.',
      evidenceData: {
        filter: result.filter,
        found:  result.found,
        probe_used: result.probe_used ?? null,
        total:  result.total ?? null,
        all_calls: result.calls ?? null,
        probes: result.probes,
        wxc_svc_message: result.message ?? null,
      },
      kind: 'wxc-call',
      analysisMode: 'action-items',
    });
  }

  // Per-row Call detail handoff.
  function handoffCall(call: Record<string, any>, idx: number) {
    const id = pickField(call, ['id', 'callId', 'callID', 'correlationId', 'correlation_id']) ?? `row-${idx + 1}`;
    onAnalyze({
      id: `wxc-call-detail-${idx}-${Date.now()}`,
      severity: 'warn',
      title: `WxC Call detail — ${id}`,
      evidence: `Single CDR row #${idx + 1} from the ${result?.calls?.length ?? 0}-row result set. probe_used=${result?.probe_used ?? '—'}.`,
      action: 'Analyze with Claude — return root cause as action items.',
      evidenceData: {
        filter: result?.filter,
        probe_used: result?.probe_used ?? null,
        call,
      },
      kind: 'wxc-call-detail',
      analysisMode: 'action-items',
    });
  }

  return (
    <div className="card p-4 space-y-5">
      <div>
        <div className="text-sm font-semibold">WxC Call Troubleshooting</div>
        <div className="text-[11px] text-gray-500">
          Webex Calling Detailed Call History (CDR) search — modelled on Control Hub's "Meetings &amp; Calls" view.
          Filter by Calling From / Called To and a date range. Click any row for the full Call detail + "Analyze with Claude" hand-off.
        </div>
      </div>

      {/* Search inputs */}
      <section className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">Search — by phone number + date range</div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <input
            value={callingFrom}
            onChange={(e) => setCallingFrom(e.target.value)}
            placeholder="Calling From (e.g. +15625709994)"
            className="px-3 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono"
          />
          <input
            value={calledTo}
            onChange={(e) => setCalledTo(e.target.value)}
            placeholder="Called To (e.g. +15625706100)"
            className="px-3 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono"
          />
          <label className="flex items-center gap-2 text-xs">
            <span className="text-gray-500 whitespace-nowrap">From</span>
            <input
              type="datetime-local"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="flex-1 px-2 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs"
            />
          </label>
          <label className="flex items-center gap-2 text-xs">
            <span className="text-gray-500 whitespace-nowrap">To</span>
            <input
              type="datetime-local"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="flex-1 px-2 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs"
            />
          </label>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={runSearch}
            disabled={loading || (!callingFrom.trim() && !calledTo.trim())}
            className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-sm font-semibold hover:opacity-90 disabled:opacity-40 transition"
          >
            {loading ? 'Probing…' : 'Search'}
          </button>
          <span className="text-[11px] text-gray-500">
            CDR search requires the <code className="font-mono">spark-analytics:cdr_read</code> Service App scope.
            If the probe returns no 2xx, that scope is the most common cause.
          </span>
        </div>
        {error && <div className="text-xs text-rose-600 dark:text-rose-300">{error}</div>}
      </section>

      {/* Results */}
      {result && (
        <WxcCallResults
          result={result}
          onOpenDetail={(i) => setOpenCallIdx(i)}
          onAnalyzeAll={handoffAll}
          activeFindingId={activeFindingId}
          openCallIdx={openCallIdx}
        />
      )}

      {/* Call detail drill-down */}
      {result?.found && result.calls && openCallIdx != null && result.calls[openCallIdx] && (
        <WxcCallDetail
          call={result.calls[openCallIdx] as Record<string, any>}
          index={openCallIdx}
          probeUsed={result.probe_used ?? ''}
          onAnalyze={() => handoffCall(result.calls![openCallIdx] as Record<string, any>, openCallIdx)}
          onClose={() => setOpenCallIdx(null)}
          activeFindingId={activeFindingId}
        />
      )}
    </div>
  );
}

function WxcCallResults({ result, onOpenDetail, onAnalyzeAll, openCallIdx }:
  { result: WxcCallsResponse; onOpenDetail: (i: number) => void; onAnalyzeAll: () => void; activeFindingId: string | null; openCallIdx: number | null }) {
  const rows = (result.calls ?? []) as Array<Record<string, any>>;
  return (
    <div className="border border-tnh-border dark:border-tnh-border-dark rounded-md overflow-hidden">
      <div className="px-3 py-2 flex items-center justify-between gap-3 flex-wrap bg-gray-50 dark:bg-gray-900/40">
        <div className="text-xs">
          {result.found
            ? <span className="text-emerald-700 dark:text-emerald-300 font-medium">{result.total ?? 0} call(s) · probe used <code className="font-mono break-all">{result.probe_used}</code></span>
            : <span className="text-rose-700 dark:text-rose-300 font-medium">No reachable Webex Calling Detailed Call History for this filter</span>}
        </div>
        {result.found && rows.length > 0 && (
          <button onClick={onAnalyzeAll} className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-xs font-semibold hover:opacity-90 transition">
            Analyze all with Claude →
          </button>
        )}
      </div>

      {result.found && rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-y border-tnh-border dark:border-tnh-border-dark">
              <tr>
                {['#', 'Status', 'Quality', 'Timestamp', 'Caller #', 'Caller name', 'Callee #', 'Callee name', 'Duration', 'Location', 'Correlation ID', ''].map((c) => (
                  <th key={c} className="text-left px-3 py-1.5 font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 100).map((r, i) => {
                const isOpen = openCallIdx === i;
                return (
                  <tr key={i} className={`border-t border-tnh-border dark:border-tnh-border-dark cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/40 ${isOpen ? 'bg-tnh-accent/5' : ''}`}
                      onClick={() => onOpenDetail(i)}>
                    <td className="px-3 py-1.5 text-gray-500">{i + 1}</td>
                    <td className="px-3 py-1.5">{pickField(r, ['status', 'callOutcome', 'releaseTime', 'finalReason', 'answeredIndicator']) ?? '—'}</td>
                    <td className="px-3 py-1.5">{pickField(r, ['mediaQuality', 'audioQualityIndicator', 'qualityIndicator', 'mos']) ?? '—'}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{pickField(r, ['startTime', 'originatingTime', 'callStartTime', 'startedDateTime', 'callTime']) ?? '—'}</td>
                    <td className="px-3 py-1.5 font-mono">{pickField(r, ['callingNumber', 'callingLineId', 'from', 'callingPartyNumber']) ?? '—'}</td>
                    <td className="px-3 py-1.5">{pickField(r, ['callingName', 'callingUserName', 'callingDisplayName', 'fromName']) ?? '—'}</td>
                    <td className="px-3 py-1.5 font-mono">{pickField(r, ['calledNumber', 'calledLineId', 'to', 'dialedDigits', 'calledPartyNumber']) ?? '—'}</td>
                    <td className="px-3 py-1.5">{pickField(r, ['calledName', 'calledUserName', 'calledDisplayName', 'toName']) ?? '—'}</td>
                    <td className="px-3 py-1.5">{pickField(r, ['duration', 'callDuration', 'durationSeconds', 'connectedDuration']) ?? '—'}</td>
                    <td className="px-3 py-1.5">{pickField(r, ['locationName', 'siteName', 'userLocation', 'location']) ?? '—'}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px]">{(pickField(r, ['correlationId', 'correlation_id', 'callId', 'localCallId']) ?? '—').slice(0, 18)}</td>
                    <td className="px-3 py-1.5 text-right">
                      <button onClick={(e) => { e.stopPropagation(); onOpenDetail(i); }} className="px-2 py-1 rounded text-[11px] font-semibold bg-tnh-accent text-white hover:opacity-90">
                        Details →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length > 100 && (
            <div className="px-3 py-1.5 text-[11px] text-gray-500 border-t border-tnh-border dark:border-tnh-border-dark">
              … and {rows.length - 100} more rows hidden. Use "Analyze all" to ship the full set.
            </div>
          )}
        </div>
      ) : (
        <div className="px-3 py-2 space-y-2">
          {result.message && <div className="text-xs text-gray-700 dark:text-gray-300">{result.message}</div>}
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Probes</div>
          <ProbeTrail probes={result.probes} />
        </div>
      )}
    </div>
  );
}

function WxcCallDetail({ call, index, probeUsed, onAnalyze, onClose, activeFindingId }:
  { call: Record<string, any>; index: number; probeUsed: string; onAnalyze: () => void; onClose: () => void; activeFindingId: string | null }) {
  function onDownload() {
    const id = pickField(call, ['id', 'callId', 'correlationId']) ?? `row-${index + 1}`;
    const body = safeStringify({ source: 'TriNetHub WxC Call Troubleshooting', probe_used: probeUsed, call });
    const blob = new Blob([body], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trinethub-wxc-call-${slug(String(id))}-${stamp(Date.now())}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  // Render the well-known fields in a labelled grid, then the rest of the
  // record as raw JSON (any extra columns the API decided to surface).
  // HONEST NOTE: the hop-by-hop media-path diagram is a Control Hub feature
  // and most CDR endpoints DO NOT expose it. We render whatever is in the
  // record and the raw JSON below covers the gap.
  const labels: Array<[string, string[]]> = [
    ['Correlation ID',  ['correlationId', 'correlation_id', 'localCallId']],
    ['Interaction ID',  ['interactionId', 'callId', 'callID']],
    ['Calling date/time', ['startTime', 'originatingTime', 'callStartTime', 'startedDateTime']],
    ['Session type',    ['sessionType', 'callType', 'direction']],
    ['Caller name',     ['callingName', 'callingUserName', 'callingDisplayName', 'fromName']],
    ['Caller number',   ['callingNumber', 'callingLineId', 'from', 'callingPartyNumber']],
    ['Called number',   ['calledNumber', 'calledLineId', 'to', 'calledPartyNumber']],
    ['Dialed digits',   ['dialedDigits', 'dialedNumber']],
    ['Calling type',    ['callingType', 'originatorType']],
    ['Route group',     ['routeGroup', 'routeGroupName']],
    ['Codec',           ['codec', 'audioCodec']],
    ['Trunk',           ['trunkName', 'trunk']],
    ['Local SIP session ID',  ['localSessionId',  'sipLocalSessionId',  'sipCallId']],
    ['Remote SIP session ID', ['remoteSessionId', 'sipRemoteSessionId', 'remoteCallId']],
    ['Location',        ['locationName', 'siteName', 'userLocation', 'location']],
    ['Media quality',   ['mediaQuality', 'audioQualityIndicator', 'qualityIndicator', 'mos']],
    ['Duration',        ['duration', 'callDuration', 'durationSeconds', 'connectedDuration']],
    ['Release reason',  ['releaseReason', 'finalReason', 'callOutcome']],
  ];
  const present  = labels.filter(([, names]) => pickField(call, names) != null);
  const missing  = labels.filter(([, names]) => pickField(call, names) == null).map(([l]) => l);
  const isActive = activeFindingId?.startsWith(`wxc-call-detail-${index}-`);

  return (
    <div className="border border-tnh-border dark:border-tnh-border-dark rounded-md overflow-hidden">
      <div className="px-3 py-2 flex items-center justify-between gap-3 flex-wrap bg-gray-50 dark:bg-gray-900/40">
        <div className="text-sm font-semibold">Call detail — row #{index + 1}</div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={onDownload}
                  className="px-3 py-1.5 rounded-md bg-gray-200 dark:bg-gray-700 text-xs font-semibold hover:opacity-90 transition">
            Download .json
          </button>
          <button onClick={onAnalyze}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${isActive ? 'bg-tnh-accent/20 text-tnh-accent' : 'bg-tnh-accent text-white hover:opacity-90'}`}>
            Analyze with Claude →
          </button>
          <button onClick={onClose} className="text-xs text-gray-500 hover:underline">Close</button>
        </div>
      </div>
      <div className="px-3 py-2 space-y-3">
        <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
          {present.map(([label, names]) => (
            <div key={label} className="flex gap-2 min-w-0">
              <span className="text-gray-500 whitespace-nowrap">{label}:</span>
              <span className="font-mono truncate">{pickField(call, names)}</span>
            </div>
          ))}
        </div>
        {missing.length > 0 && (
          <div className="text-[11px] text-gray-500">
            <span className="font-semibold">Not exposed by this endpoint:</span> {missing.join(' · ')}.
            {' '}Hop-by-hop media-path is a Control Hub-only view — not in any public CDR API.
          </div>
        )}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Raw record</div>
          <pre className="text-[11px] font-mono whitespace-pre-wrap bg-gray-50 dark:bg-gray-900/40 p-2 rounded max-h-72 overflow-auto">{safeStringify(call)}</pre>
        </div>
      </div>
    </div>
  );
}

// Format an epoch ms as a value the <input type="datetime-local"> element accepts.
// (datetime-local wants `YYYY-MM-DDTHH:MM` in LOCAL time — no timezone suffix.)
function isoLocalDatetime(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ---------------------------------------------------------------------------
// HAR + log parsers (client-side). Malformed input → parseError on the
// Attachment instead of throwing — the rest of the brief should still build.
// ---------------------------------------------------------------------------
function parseHar(name: string, sizeBytes: number, text: string): Attachment {
  try {
    const har = JSON.parse(text);
    const entries: any[] = har?.log?.entries ?? [];
    const failed: HarErrorEntry[] = [];
    for (const e of entries) {
      const status = Number(e?.response?.status ?? 0);
      // Treat 0 (cancelled / no-response) and any 4xx/5xx as failed.
      if (status === 0 || status >= 400) {
        const trackingHeader = pickHeader(e?.response?.headers, ['trackingid', 'TrackingId', 'X-Cisco-TrackingID', 'x-trackingid']);
        const body = e?.response?.content?.text ?? e?.response?._error ?? '';
        failed.push({
          url: String(e?.request?.url ?? ''),
          method: String(e?.request?.method ?? ''),
          status,
          statusText: String(e?.response?.statusText ?? ''),
          trackingid: trackingHeader,
          timeMs: typeof e?.time === 'number' ? e.time : null,
          errorBodyExcerpt: typeof body === 'string' ? body.slice(0, 600) : safeStringify(body).slice(0, 600),
          startedDateTime: String(e?.startedDateTime ?? ''),
        });
      }
    }
    return {
      id: 'a-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6),
      name, kind: 'har', sizeBytes,
      totalRequests: entries.length,
      failedRequests: failed.length,
      harErrors: failed,
      rawText: text,
    };
  } catch (e: any) {
    return {
      id: 'a-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6),
      name, kind: 'har', sizeBytes,
      parseError: `HAR parse failed: ${e?.message ?? String(e)}`,
      rawText: text,
    };
  }
}

function parseLog(name: string, sizeBytes: number, text: string): Attachment {
  const lines = text.split(/\r?\n/);
  const errors: LogErrorEntry[] = [];
  const levelRe = /\b(FATAL|CRITICAL|ERROR|ERR|WARN(?:ING)?|EXCEPTION)\b/;
  for (let i = 0; i < lines.length; i++) {
    const ln = lines[i] ?? '';
    const m = ln.match(levelRe);
    if (!m) continue;
    const ctx: string[] = [];
    for (let j = Math.max(0, i - 2); j <= Math.min(lines.length - 1, i + 2); j++) {
      ctx.push(`${j === i ? '>' : ' '} ${String(j + 1).padStart(5)}  ${lines[j] ?? ''}`);
    }
    errors.push({ lineNumber: i + 1, level: m[1] ?? 'ERROR', line: ln, context: ctx });
    if (errors.length >= 400) break;     // hard cap; an admin attaching a log this dense should narrow it
  }
  return {
    id: 'a-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6),
    name, kind: 'log', sizeBytes,
    totalLines: lines.length,
    errorLines: errors.length,
    logErrors: errors,
  };
}

function pickHeader(headers: any[] | undefined, names: string[]): string | null {
  if (!Array.isArray(headers)) return null;
  const lowerWanted = names.map((n) => n.toLowerCase());
  for (const h of headers) {
    const n = String(h?.name ?? '').toLowerCase();
    if (lowerWanted.includes(n)) return String(h?.value ?? '');
  }
  return null;
}

async function sniffKind(f: File): Promise<Attachment['kind']> {
  // Read just the head to decide. HARs are JSON with `log.version` / `log.entries`.
  try {
    const head = await f.slice(0, 4096).text();
    const t = head.trim();
    if (t.startsWith('{') && /"log"\s*:/.test(t)) return 'har';
  } catch { /* fall through */ }
  return 'log';
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
